import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MobileContractSignPopup from './MobileContractSignPopup';
import { contractApi, type Contract } from '../../services/contractApi';
import { getTenantPayRoomCode } from '../../utils/tenantPayRoom';
import { requestDashboardRefresh } from '../../utils/dashboardRefresh';
import { getPropertyDisplayHeading } from '../../utils/propertyAddress';

const CLOSED_STATUSES = new Set(['cancelled', 'completed', 'expired']);

type LandlordRoomItem = {
  contractId: number;
  roomNumber: string;
  address: string;
  tenantName: string;
};

function apiErrorMessage(err: unknown, fallback: string): string {
  const axiosDetail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof axiosDetail === 'string' && axiosDetail.trim()) return axiosDetail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function displayRoomNumber(value?: string | null): string {
  return (value || '').trim() || '—';
}

function displayAddress(contract: Pick<Contract, 'full_address' | 'property_name'>): string {
  return (contract.full_address || contract.property_name || '').trim() || '—';
}

function displayTenantName(value?: string | null): string {
  return (value || '').trim() || '—';
}

function roomLabel(contract: Contract): string {
  return getPropertyDisplayHeading({
    title: contract.property_name,
    room_number: contract.room_number,
    full_address: contract.full_address,
  });
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function todayParts() {
  const now = new Date();
  return { day: now.getDate(), month: now.getMonth() + 1, year: now.getFullYear() };
}

function parseIsoDate(value?: string | null): { day: number; month: number; year: number } | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1) return null;
  return { day, month, year };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function toIsoDate(parts: { day: number; month: number; year: number }): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function formatVnDate(iso: string): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return iso;
  return `${pad2(parsed.day)}/${pad2(parsed.month)}/${parsed.year}`;
}

type Props = {
  isTenant: boolean;
  trailing?: React.ReactNode;
};

const MobileCancelContractControl: React.FC<Props> = ({ isTenant, trailing }) => {
  const { user, invalidateTenantSession } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rooms, setRooms] = useState<LandlordRoomItem[]>([]);
  const [contract, setContract] = useState<Contract | null>(null);
  const [tenantContract, setTenantContract] = useState<Contract | null>(null);
  const [signingRole, setSigningRole] = useState<'landlord' | 'tenant' | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [day, setDay] = useState(() => todayParts().day);
  const [month, setMonth] = useState(() => todayParts().month);
  const [year, setYear] = useState(() => todayParts().year);

  const dateSource = isTenant ? tenantContract?.end_date : contract?.end_date;
  const maxDate = useMemo(() => {
    const today = todayParts();
    const end = parseIsoDate(dateSource);
    if (end) return end;
    return { day: today.day, month: today.month, year: today.year + 2 };
  }, [dateSource]);

  const minDate = todayParts();
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let value = minDate.year; value <= maxDate.year; value += 1) years.push(value);
    if (!years.length) years.push(minDate.year);
    return years;
  }, [maxDate.year, minDate.year]);

  const dayCount = daysInMonth(year, month);

  useEffect(() => {
    if (day > dayCount) setDay(dayCount);
  }, [day, dayCount]);

  const close = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setError(null);
    setSuccess(null);
    setRooms([]);
    setContract(null);
    setTenantContract(null);
    setShowCancel(false);
    setSigningRole(null);
  }, [busy]);

  const openContractById = useCallback(async (contractId: number, extras?: Partial<Contract>) => {
    setLoadingContract(true);
    setError(null);
    try {
      const detail = await contractApi.getContract(contractId);
      setContract({ ...detail, ...extras });
      const scheduled = parseIsoDate(detail.scheduled_cancel_date);
      const parts = scheduled || todayParts();
      setYear(parts.year);
      setMonth(parts.month);
      setDay(parts.day);
    } catch (err) {
      setContract(null);
      setError(apiErrorMessage(err, 'Không tải được hợp đồng.'));
    } finally {
      setLoadingContract(false);
    }
  }, []);

  const loadLandlordRooms = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const data = (await contractApi.getContracts()) as Contract[] | { contracts?: Contract[] };
      const list = Array.isArray(data) ? data : data.contracts || [];
      const userId = Number(user?.id);
      const items = list
        .filter((item) => !CLOSED_STATUSES.has(item.status))
        .filter((item) => {
          if (!Number.isFinite(userId)) return true;
          return item.landlord_id === userId || item.broker_id === userId;
        })
        .map((item) => ({
          contractId: item.id,
          roomNumber: displayRoomNumber(item.room_number),
          address: displayAddress(item),
          tenantName: displayTenantName(item.tenant_name),
        }));
      setRooms(items);
      if (!items.length) {
        setError('Không có phòng nào đang có hợp đồng.');
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Không tải được danh sách phòng.'));
      setRooms([]);
    } finally {
      setLoadingList(false);
    }
  }, [user?.id]);

  const loadTenantContract = useCallback(async () => {
    const roomCode = getTenantPayRoomCode(user?.id);
    if (!roomCode) {
      setError('Không xác định được phòng đang thuê. Hãy đăng xuất rồi quét lại QR.');
      setTenantContract(null);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setError(null);
    try {
      const current = await contractApi.getTenantCurrentContract();
      if (!current) {
        throw new Error('Phòng này không có hợp đồng đang hiệu lực để hủy.');
      }
      setTenantContract(current);
      const scheduled = parseIsoDate(current.scheduled_cancel_date);
      const parts = scheduled || todayParts();
      setYear(parts.year);
      setMonth(parts.month);
      setDay(parts.day);
    } catch (err) {
      setTenantContract(null);
      setError(apiErrorMessage(err, 'Không tải được hợp đồng phòng đang thuê.'));
    } finally {
      setLoadingList(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    if (isTenant) {
      void loadTenantContract();
      return;
    }
    void loadLandlordRooms();
  }, [isTenant, loadLandlordRooms, loadTenantContract, open]);

  const currentUserId = user?.id ? Number(user.id) : null;
  const isLandlordSigner = Boolean(
    !isTenant &&
      contract &&
      currentUserId &&
      (currentUserId === contract.landlord_id || currentUserId === contract.broker_id),
  );
  const canSignLandlord = Boolean(isLandlordSigner && !contract?.landlord_signature_image);
  const canSignTenant = Boolean(isLandlordSigner && !contract?.tenant_signature_image);

  const handleSaveSignature = async (role: 'landlord' | 'tenant', image: string) => {
    if (!contract || !isLandlordSigner) return;
    setSigningRole(role);
    setError(null);
    try {
      const result = await contractApi.confirmSignature(contract.id, role, image);
      setContract(result.contract);
      setSuccess(
        result.both_signed
          ? 'Đã ký đủ hai ô. Hợp đồng có hiệu lực.'
          : 'Đã lưu chữ ký. Ký tiếp ô còn lại.',
      );
    } catch (err) {
      setError(apiErrorMessage(err, 'Không lưu được chữ ký. Vui lòng thử lại.'));
    } finally {
      setSigningRole(null);
    }
  };

  const handleTenantConfirm = useCallback(async () => {
    if (!tenantContract) {
      setError('Không xác định được hợp đồng phòng đang thuê.');
      return;
    }
    const selected = { day, month, year };
    if (selected.day > daysInMonth(selected.year, selected.month)) {
      setError('Ngày không hợp lệ. Vui lòng chọn lại.');
      return;
    }
    const iso = toIsoDate(selected);
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await contractApi.scheduleContractCancel(
        tenantContract.id,
        iso,
        'Khách thuê đặt lịch hủy hợp đồng từ app',
      );
      if (result.applied) {
        await invalidateTenantSession('Đã hủy hợp đồng. Vui lòng quét QR nếu thuê phòng khác.');
        return;
      }
      setSuccess(result.message || `Hợp đồng sẽ tự hủy vào ${formatVnDate(result.cancel_date)}`);
      setTenantContract(result.contract);
      setBusy(false);
    } catch (err) {
      setBusy(false);
      setError(apiErrorMessage(err, 'Không đặt được ngày hủy hợp đồng. Vui lòng thử lại.'));
    }
  }, [day, invalidateTenantSession, month, tenantContract, year]);

  const handleLandlordConfirm = useCallback(async () => {
    if (!contract) return;
    setBusy(true);
    setError(null);
    try {
      await contractApi.cancelContract(contract.id, 'Chủ nhà hủy hợp đồng từ app');
      requestDashboardRefresh();
      setRooms((prev) => prev.filter((item) => item.contractId !== contract.id));
      setContract(null);
      setShowCancel(false);
      setBusy(false);
      setSuccess('Đã hủy hợp đồng.');
    } catch (err) {
      setBusy(false);
      setError(apiErrorMessage(err, 'Không hủy được hợp đồng. Vui lòng thử lại.'));
    }
  }, [contract]);

  const openFlow = () => {
    setError(null);
    setSuccess(null);
    setContract(null);
    setTenantContract(null);
    setShowCancel(false);
    setOpen(true);
  };

  const selectedIso = toIsoDate({ day, month, year });
  const isToday = selectedIso === toIsoDate(minDate);
  const showingDocument = Boolean(contract) && !loadingContract;

  if (isTenant) {
    return (
      <>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={openFlow}
            className="rounded-xl bg-[#C4A35A] px-2.5 py-2 text-[11px] font-medium text-white"
          >
            Hủy hợp đồng
          </button>
          {trailing}
        </div>

        {open && (
          <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
              <div className="border-b border-black/5 px-4 py-3">
                <h2 className="text-base font-semibold text-black">Hủy hợp đồng</h2>
                <p className="mt-0.5 text-xs text-black/50">
                  Chọn ngày tháng năm. Đến ngày đó hợp đồng sẽ tự hủy.
                </p>
              </div>

              <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
                {error && (
                  <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
                )}
                {success && (
                  <p className="mb-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p>
                )}

                {loadingList && (
                  <div className="flex justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C4A35A] border-t-transparent" />
                  </div>
                )}

                {!loadingList && tenantContract && (
                  <div className="space-y-3">
                    <p className="text-sm text-black/80">
                      Phòng đang thuê: <span className="font-semibold">{roomLabel(tenantContract)}</span>
                    </p>
                    {tenantContract.scheduled_cancel_date && (
                      <p className="text-xs text-black/50">
                        Đã đặt hủy vào {formatVnDate(tenantContract.scheduled_cancel_date)}
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <label className="text-xs font-medium text-black/60">
                        Ngày
                        <select
                          value={day}
                          onChange={(event) => setDay(Number(event.target.value))}
                          className="mt-1 w-full rounded-xl border border-black/10 bg-white px-2 py-2 text-sm text-black"
                        >
                          {Array.from({ length: dayCount }, (_, index) => index + 1).map((value) => (
                            <option key={value} value={value}>
                              {pad2(value)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-medium text-black/60">
                        Tháng
                        <select
                          value={month}
                          onChange={(event) => setMonth(Number(event.target.value))}
                          className="mt-1 w-full rounded-xl border border-black/10 bg-white px-2 py-2 text-sm text-black"
                        >
                          {Array.from({ length: 12 }, (_, index) => index + 1).map((value) => (
                            <option key={value} value={value}>
                              {pad2(value)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-medium text-black/60">
                        Năm
                        <select
                          value={year}
                          onChange={(event) => setYear(Number(event.target.value))}
                          className="mt-1 w-full rounded-xl border border-black/10 bg-white px-2 py-2 text-sm text-black"
                        >
                          {yearOptions.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <p className="text-sm text-black/80">
                      {isToday
                        ? 'Bạn đang chọn hôm nay — hợp đồng sẽ hủy ngay.'
                        : `Hợp đồng sẽ tự hủy vào ${formatVnDate(selectedIso)}.`}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 border-t border-black/5 px-4 py-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={close}
                  className="rounded-xl border border-black/10 px-3 py-2 text-sm font-medium text-black/70"
                >
                  Đóng
                </button>
                {tenantContract && (
                  <button
                    type="button"
                    disabled={busy || loadingList}
                    onClick={() => void handleTenantConfirm()}
                    className="rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {busy ? 'Đang lưu…' : isToday ? 'Hủy hôm nay' : 'Xác nhận ngày hủy'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={openFlow}
          className="rounded-xl bg-[#C4A35A] px-2.5 py-2 text-[11px] font-medium text-white"
        >
          Hợp đồng
        </button>
        {trailing}
      </div>

      <MobileContractSignPopup
        open={open}
        title="Hợp đồng"
        onClose={close}
        contract={showingDocument ? contract : null}
        loading={loadingList || loadingContract}
        error={error}
        success={success}
        canSignLandlord={canSignLandlord}
        canSignTenant={canSignTenant}
        signingRole={signingRole}
        onSaveSignature={handleSaveSignature}
        toolbar={
          contract ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setContract(null);
                setShowCancel(false);
                setError(null);
                setSuccess(null);
              }}
              className="rounded-xl border border-black/10 px-3 py-1.5 text-sm font-medium text-black/70"
            >
              Chọn phòng
            </button>
          ) : null
        }
      >
        {!contract && !loadingList && (
          <ul className="mb-3 space-y-2">
            {rooms.map((room) => (
              <li key={room.contractId}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void openContractById(room.contractId)}
                  className="w-full rounded-xl border border-black/10 bg-[#f9fafb] px-3 py-3 text-left"
                >
                  <p className="text-sm text-black">
                    <span className="text-black/50">Số phòng:</span> {room.roomNumber}
                  </p>
                  <p className="mt-1 break-words text-sm text-black">
                    <span className="text-black/50">Địa chỉ:</span> {room.address}
                  </p>
                  <p className="mt-1 text-sm text-black">
                    <span className="text-black/50">Người thuê:</span> {room.tenantName}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}

        {showingDocument && contract && (
          <div className="mb-4 space-y-3">
            {!showCancel ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setShowCancel(true);
                }}
                className="w-full rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700"
              >
                Hủy hợp đồng
              </button>
            ) : (
              <div className="space-y-3 rounded-2xl border border-red-100 bg-[#f9fafb] p-3">
                <p className="text-sm font-semibold text-black">Hủy hợp đồng</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setShowCancel(false)}
                    className="flex-1 rounded-xl border border-black/10 px-3 py-2 text-sm font-medium text-black/70"
                  >
                    Không
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleLandlordConfirm()}
                    className="flex-1 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {busy ? 'Đang hủy…' : 'Xác nhận hủy'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </MobileContractSignPopup>
    </>
  );
};

export default MobileCancelContractControl;
